import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Upload, Loader2, Trash2, LayoutGrid, Maximize2, DoorOpen, Square,
  ChevronDown, ChevronUp, Home, Ruler, Eye, X, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const ROOM_ICONS = {
  wohnraum: Home, schlafzimmer: Home, küche: Home, bad: DoorOpen,
  flur: DoorOpen, balkon: Maximize2, keller: Square, garage: Square,
  büro: LayoutGrid, abstellraum: Square, terrasse: Maximize2
};

const ROOM_COLORS = {
  wohnraum: 'bg-blue-50 border-blue-200 text-blue-700',
  schlafzimmer: 'bg-purple-50 border-purple-200 text-purple-700',
  küche: 'bg-orange-50 border-orange-200 text-orange-700',
  bad: 'bg-cyan-50 border-cyan-200 text-cyan-700',
  flur: 'bg-gray-50 border-gray-200 text-gray-700',
  balkon: 'bg-green-50 border-green-200 text-green-700',
  keller: 'bg-stone-50 border-stone-200 text-stone-700',
  garage: 'bg-slate-50 border-slate-200 text-slate-700',
  büro: 'bg-indigo-50 border-indigo-200 text-indigo-700',
  abstellraum: 'bg-amber-50 border-amber-200 text-amber-700',
  terrasse: 'bg-emerald-50 border-emerald-200 text-emerald-700',
};

const RoomCard = ({ room, index }) => {
  const [open, setOpen] = useState(false);
  const Icon = ROOM_ICONS[room.type] || Square;
  const colors = ROOM_COLORS[room.type] || 'bg-gray-50 border-gray-200 text-gray-700';

  return (
    <div className={`border rounded-lg overflow-hidden ${colors.split(' ').slice(0, 2).join(' ')}`} data-testid={`room-${index}`}>
      <div className="p-3 flex items-center justify-between cursor-pointer" onClick={() => setOpen(!open)}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{room.name}</h4>
            <p className="text-sm text-gray-500">{room.inner_width_m} x {room.inner_length_m} m</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-gray-900">{room.inner_area_sqm} m²</span>
          {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>
      {open && (
        <div className="px-3 pb-3 border-t bg-white/50">
          <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Breite (innen):</span><span className="font-medium">{room.inner_width_m} m</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Länge (innen):</span><span className="font-medium">{room.inner_length_m} m</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Fläche:</span><span className="font-medium">{room.inner_area_sqm} m²</span></div>
            {room.ceiling_height_m && <div className="flex justify-between"><span className="text-gray-500">Deckenhöhe:</span><span className="font-medium">{room.ceiling_height_m} m</span></div>}
            {room.windows !== undefined && <div className="flex justify-between"><span className="text-gray-500">Fenster:</span><span className="font-medium">{room.windows}</span></div>}
            {room.doors !== undefined && <div className="flex justify-between"><span className="text-gray-500">Türen:</span><span className="font-medium">{room.doors}</span></div>}
          </div>
          {room.notes && <p className="mt-2 text-xs text-gray-500 italic">{room.notes}</p>}
        </div>
      )}
    </div>
  );
};

const AdminGrundriss = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [notes, setNotes] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const fileRef = useRef(null);

  const loadPlans = async () => {
    try {
      const res = await api.get('/admin/grundriss');
      setPlans(res.data || []);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { loadPlans(); }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Nur Bilder (JPG, PNG) werden unterstützt'); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error('Datei zu groß (max. 20 MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      setPreview({ url: reader.result, base64, name: file.name });
    };
    reader.readAsDataURL(file);
  };

  const analyzeFloorPlan = async () => {
    if (!preview) { toast.error('Bitte Grundriss hochladen'); return; }
    setAnalyzing(true);
    try {
      const res = await api.post('/admin/grundriss/analyze', {
        image_base64: preview.base64,
        notes
      });
      toast.success('Grundriss analysiert!');
      setSelectedPlan({ id: res.data.id, analysis: res.data.analysis, created_at: new Date().toISOString(), notes });
      setPreview(null);
      setNotes('');
      if (fileRef.current) fileRef.current.value = '';
      loadPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analyse fehlgeschlagen');
    }
    setAnalyzing(false);
  };

  const deletePlan = async (id) => {
    if (!confirm('Analyse löschen?')) return;
    try {
      await api.delete(`/admin/grundriss/${id}`);
      toast.success('Gelöscht');
      if (selectedPlan?.id === id) setSelectedPlan(null);
      loadPlans();
    } catch (e) { toast.error('Fehler'); }
  };

  const a = selectedPlan?.analysis;
  const rooms = a?.rooms || [];
  const totalArea = a?.total_area_sqm || rooms.reduce((s, r) => s + (r.inner_area_sqm || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 -m-4 lg:-m-8 p-4 sm:p-6 overflow-y-auto">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="bg-gradient-to-r from-[#0e7490] to-[#06b6d4] rounded-xl p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3" data-testid="grundriss-title">
                <LayoutGrid className="w-8 h-8" /> Grundriss-Analyse
              </h1>
              <p className="text-white/80 mt-1">KI-gestützte Raumvermessung aus Grundriss-Bildern</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Upload + History */}
        <div className="lg:col-span-1 space-y-4">
          {/* Upload Card */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Upload className="w-4 h-4" /> Grundriss hochladen</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${preview ? 'border-cyan-400 bg-cyan-50' : 'border-gray-300 hover:border-cyan-400 hover:bg-gray-50'}`}
                onClick={() => fileRef.current?.click()}
                data-testid="upload-area"
              >
                {preview ? (
                  <div className="relative">
                    <img src={preview.url} alt="Grundriss" className="max-h-48 mx-auto rounded" />
                    <button onClick={(e) => { e.stopPropagation(); setPreview(null); if (fileRef.current) fileRef.current.value = ''; }} className="absolute top-1 right-1 bg-white rounded-full p-1 shadow"><X className="w-4 h-4" /></button>
                    <p className="text-xs text-gray-500 mt-2">{preview.name}</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Grundriss-Bild auswählen</p>
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG (max. 20 MB)</p>
                  </>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
              <div>
                <Label>Anmerkungen (optional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="z.B. Maßstab 1:100, EG, Altbau..." className="mt-1" rows={2} data-testid="notes-input" />
              </div>
              <Button onClick={analyzeFloorPlan} disabled={analyzing || !preview} className="w-full bg-[#0e7490] hover:bg-[#0c5f75] text-white" data-testid="analyze-btn">
                {analyzing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Wird analysiert...</> : <><Eye className="w-4 h-4 mr-2" /> Grundriss analysieren</>}
              </Button>
            </CardContent>
          </Card>

          {/* History */}
          <Card>
            <CardHeader><CardTitle className="text-base">Gespeicherte Analysen ({plans.length})</CardTitle></CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center p-4"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
              ) : plans.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Noch keine Analysen</p>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {plans.map(plan => {
                    const pa = plan.analysis || {};
                    const roomCount = (pa.rooms || []).length;
                    const area = pa.total_area_sqm || 0;
                    return (
                      <div key={plan.id}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedPlan?.id === plan.id ? 'border-cyan-500 bg-cyan-50 ring-1 ring-cyan-500' : 'border-gray-200 hover:border-gray-300'}`}
                        onClick={() => setSelectedPlan(plan)}
                        data-testid={`plan-${plan.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm text-gray-900">{pa.building_type || 'Grundriss'}</p>
                            <p className="text-xs text-gray-500">{roomCount} Räume, {area.toFixed(1)} m²</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {new Date(plan.created_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deletePlan(plan.id); }} className="text-red-400 hover:text-red-600 -mt-1">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right: Analysis Results */}
        <div className="lg:col-span-2">
          {!selectedPlan ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-gray-400">
                <LayoutGrid className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium">Grundriss hochladen oder Analyse auswählen</p>
                <p className="text-sm mt-1">Die KI erkennt automatisch Räume und Abmessungen</p>
              </CardContent>
            </Card>
          ) : a?.parse_error ? (
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2 text-red-600">Analyse-Ergebnis (Rohtext)</h3>
                <pre className="text-sm whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border">{a.raw_response}</pre>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-cyan-700">{totalArea.toFixed(1)}</p>
                    <p className="text-xs text-gray-500">m² Gesamt</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-blue-700">{rooms.length}</p>
                    <p className="text-xs text-gray-500">Räume</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-green-700">{a?.building_type || '—'}</p>
                    <p className="text-xs text-gray-500">Gebäudetyp</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold text-purple-700">{a?.floors || 1}</p>
                    <p className="text-xs text-gray-500">Geschosse</p>
                  </CardContent>
                </Card>
              </div>

              {/* Outer Dimensions */}
              {a?.total_outer_dimensions && (
                <Card>
                  <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2"><Maximize2 className="w-4 h-4 text-cyan-600" /> Außenmaße</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-6">
                      <div className="flex-1 bg-cyan-50 border border-cyan-200 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-cyan-800">{a.total_outer_dimensions.width_m} m</p>
                        <p className="text-sm text-cyan-600">Breite</p>
                      </div>
                      <X className="w-5 h-5 text-gray-300" />
                      <div className="flex-1 bg-cyan-50 border border-cyan-200 rounded-lg p-4 text-center">
                        <p className="text-3xl font-bold text-cyan-800">{a.total_outer_dimensions.length_m} m</p>
                        <p className="text-sm text-cyan-600">Länge</p>
                      </div>
                    </div>
                    {a?.outer_walls?.wall_thickness_cm && (
                      <p className="text-xs text-gray-500 text-center mt-2">Wandstärke: {a.outer_walls.wall_thickness_cm} cm</p>
                    )}
                    {a?.scale_info && (
                      <p className="text-xs text-gray-500 text-center mt-1">Maßstab: {a.scale_info}</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Rooms */}
              <Card>
                <CardHeader className="py-3"><CardTitle className="text-base flex items-center gap-2"><DoorOpen className="w-4 h-4 text-blue-600" /> Räume ({rooms.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {rooms.map((room, i) => <RoomCard key={i} room={room} index={i} />)}
                  </div>
                </CardContent>
              </Card>

              {/* Special Features */}
              {a?.special_features?.length > 0 && (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Besondere Merkmale:</p>
                    <div className="flex flex-wrap gap-2">
                      {a.special_features.map((f, i) => (
                        <span key={i} className="text-xs px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">{f}</span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Construction Notes */}
              {a?.construction_notes && (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm font-medium text-gray-700 mb-1">Hinweise zur Bauweise:</p>
                    <p className="text-sm text-gray-600">{a.construction_notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminGrundriss;
